import React, { useState, useEffect } from 'react';
import { X, Send, Loader2, CheckCircle2 } from 'lucide-react';

interface ContactModalProps {
    isOpen: boolean;
    onClose: () => void;
    source?: string;
}

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose, source = 'landing_page' }) => {
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [formData, setFormData] = useState({
        name: '',
        role: '',
        email: '',
        company: '',
        industry: '',
        comments: ''
    });

    // Robust state reset when modal visibility changes
    useEffect(() => {
        if (!isOpen) {
            setStatus('idle');
            setFormData({
                name: '',
                role: '',
                email: '',
                company: '',
                industry: '',
                comments: ''
            });
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!EMAIL_REGEX.test(formData.email)) {
            setStatus('error');
            return;
        }

        setStatus('loading');

        try {
            const API_BASE_URL = import.meta.env.VITE_API_URL || '';
            const PROXY_URL = `${API_BASE_URL}/api/webhooks/lead`;


            const response = await fetch(PROXY_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                mode: 'cors', // Explicitly set cors mode
                body: JSON.stringify({ ...formData, source, timestamp: new Date().toISOString() })
            });

            if (response.ok) {
                setStatus('success');
            } else {
                setStatus('error');
            }
        } catch (error) {
            setStatus('error');
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-background-dark/80 backdrop-blur-sm">
            <div className="relative w-full max-w-lg bg-surface-dark border border-white/10 rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">

                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-6 right-6 p-2 hover:bg-white/5 rounded-full transition-colors text-slate-400 hover:text-white"
                >
                    <X className="w-6 h-6" />
                </button>

                <div className="p-8 md:p-10">
                    {status === 'success' ? (
                        <div className="text-center py-10 animate-in fade-in slide-in-from-bottom-4">
                            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                <CheckCircle2 className="w-10 h-10 text-primary" />
                            </div>
                            <h3 className="text-2xl font-bold mb-4 font-montserrat">¡Solicitud Recibida!</h3>
                            <p className="text-slate-400 mb-8">
                                Gracias por tu interés. En breve me pondré en contacto contigo para coordinar nuestra consultoría estratégica.
                            </p>
                            <button
                                onClick={onClose}
                                className="w-full py-4 bg-primary hover:bg-primary-hover text-white font-bold rounded-2xl transition-all"
                            >
                                Cerrar
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="mb-8">
                                <h3 className="text-2xl font-bold mb-2 font-montserrat tracking-tight text-white">Agendar Consultoría</h3>
                                <p className="text-slate-400">Completa tus datos y diseñemos tu estrategia de QA de grado bancario.</p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider ml-1">Nombre</label>
                                        <input
                                            required
                                            type="text"
                                            placeholder="Tu nombre completo"
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all"
                                            value={formData.name}
                                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider ml-1">Cargo</label>
                                        <input
                                            required
                                            type="text"
                                            placeholder="Ej: QA Lead, CTO"
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all"
                                            value={formData.role}
                                            onChange={e => setFormData({ ...formData, role: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider ml-1">Correo Corporativo</label>
                                    <input
                                        required
                                        type="email"
                                        placeholder="email@empresa.com"
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all"
                                        value={formData.email}
                                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider ml-1">Empresa</label>
                                        <input
                                            required
                                            type="text"
                                            placeholder="Nombre de la empresa"
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all"
                                            value={formData.company}
                                            onChange={e => setFormData({ ...formData, company: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider ml-1">Industria</label>
                                        <select
                                            required
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all appearance-none"
                                            value={formData.industry}
                                            onChange={e => setFormData({ ...formData, industry: e.target.value })}
                                        >
                                            <option value="" className="bg-surface-dark">Selecciona sector</option>
                                            <option value="Banca" className="bg-surface-dark">Banca / Fintech</option>
                                            <option value="Retail" className="bg-surface-dark">Retail / E-commerce</option>
                                            <option value="Seguros" className="bg-surface-dark">Seguros</option>
                                            <option value="SaaS" className="bg-surface-dark">SaaS / Tech</option>
                                            <option value="Otro" className="bg-surface-dark">Otro</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider ml-1">¿En qué podemos ayudarte?</label>
                                    <textarea
                                        rows={3}
                                        placeholder="Cuéntanos sobre tus desafíos de calidad..."
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all resize-none"
                                        value={formData.comments}
                                        onChange={e => setFormData({ ...formData, comments: e.target.value })}
                                    />
                                </div>

                                <button
                                    disabled={status === 'loading'}
                                    type="submit"
                                    className="w-full py-4 bg-primary hover:bg-primary-hover disabled:bg-primary/50 text-white font-bold rounded-2xl flex items-center justify-center gap-2 transition-all shadow-xl shadow-primary/20 hover:shadow-primary/40 leading-none group"
                                >
                                    {status === 'loading' ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <>Confirmar Solicitud <Send className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" /></>
                                    )}
                                </button>

                                {status === 'error' && (
                                    <p className="text-center text-red-400 text-sm animate-pulse">
                                        Error al enviar. Por favor, reintenta o escríbenos a pablo.ramirez@flowspec.ai
                                    </p>
                                )}
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ContactModal;
